import React, { useEffect, useState, useMemo } from "react";
import UserDetailModal from "../components/DisabledList/UserDetailModal.js";
import PushToDisableButton from "../components/DisabledList/PushToDisableListButton.js";
import RunDisabledProcessButton from "../components/DisabledList/RunDisabledProcessButton.js";
import ClearLastDisabledButton from "../components/DisabledList/ClearLastDisabledButton.js";
import UpdateDisableListModal from "../components/DisabledList/EditDisabledListModal.js";
import CheckEnabledUser from "../components/DisabledList/CheckEnabledUserButton.js";

export default function DisabledListPage({ role }) {
  const API_URL = "https://api.wifikamu.my.id/disabled-list";
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [createdAt, setCreatedAt] = useState("");

  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const perPage = 100;

  const buildUrl = () => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (status) params.append("billing_status", status);
    if (createdAt) params.append("created_at", createdAt);

    return `${API_URL}${params.toString() ? "?" + params.toString() : ""}`;
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = buildUrl();
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const body = await res.json();
      if (!body.success) throw new Error(body.message || "API error");

      setRows(body.data || []);
      setPage(1);
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshData = () => {
    fetchData(); // fungsi fetch ulang datamu
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const clearFilters = () => {
    setQ("");
    setStatus("");
    setCreatedAt("");
    setPage(1);
    fetchData();
  };

  // client-side pagination
  const paginated = useMemo(() => {
    const start = (page - 1) * perPage;
    return rows.slice(start, start + perPage);
  }, [rows, page]);

  const totalPages = Math.max(1, Math.ceil(rows.length / perPage));

  // CSV Export
  const exportCSV = () => {
    if (!rows.length) return;
    const keys = Object.keys(rows[0]);
    const csv = [keys.join(",")]
      .concat(
        rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `disabled-list-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const openModal = (row) => {
    setSelectedRow(row);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedRow(null);
    setShowModal(false);
  };

  const handleOpen = (row) => {
    setSelected(row);
    setOpen(true);
  };

  const billingDates = [];

  for (let year = 2026; year <= 2027; year++) {
    for (let month = 1; month <= 12; month++) {
      const value = `${year}-${String(month).padStart(2, "0")}-07`;

      billingDates.push({
        value,
        label: `07/${String(month).padStart(2, "0")}/${year}`,
      });
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <header className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Disabled List</h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Disabled list akan diterbitkan setiap tanggal 7, data didasarkan
              pada data yang exists pada mikrotik router.
            </p>
          </div>

          {["admin", "billing", "NOC"].includes(role) && (
            <div className="flex items-center gap-3">
              {/* Khusus admin */}
              {role === "admin" && (
                <>
                  <PushToDisableButton token={token} onSuccess={refreshData} />
                  <ClearLastDisabledButton
                    token={token}
                    onSuccess={refreshData}
                  />
                  <RunDisabledProcessButton
                    token={token}
                    onSuccess={refreshData}
                  />
                  <CheckEnabledUser token={token} onSuccess={refreshData} />
                </>
              )}

              {/* Admin, Billing, NOC */}
              <button
                onClick={exportCSV}
                className="px-3 py-2 rounded-md bg-indigo-600 text-white text-sm shadow-sm"
              >
                Export CSV
              </button>
            </div>
          )}
        </header>

        <section className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm mb-6">
          <form
            className="flex flex-col md:flex-row gap-3 items-end"
            onSubmit={handleSearch}
          >
            <div className="flex-1">
              <label className="text-sm block mb-1">
                Search (name or cust_id)
              </label>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full p-2 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
                placeholder="e.g. 1001 or WFH"
              />
            </div>

            <div className="w-40">
              <label className="text-sm block mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
              >
                <option value="">Any</option>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
                <option value="not found">Not Found</option>
                <option value="keep enabled">Keep Enabled</option>
                <option value="suspend">Suspend</option>
              </select>
            </div>

            {/* <div className="w-44">
              <label className="text-sm block mb-1">Created At</label>
              <input
                type="date"
                value={createdAt}
                onChange={(e) => setCreatedAt(e.target.value)}
                className="w-full p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
              />
            </div> */}

            <div className="w-44">
              <label className="text-sm block mb-1">Pilih Periode</label>

              <select
                value={createdAt}
                onChange={(e) => setCreatedAt(e.target.value)}
                className="w-full p-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
              >
                <option value="">Any</option>

                {billingDates.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded-md shadow-sm"
                disabled={loading}
              >
                {loading ? "Loading..." : "Search"}
              </button>

              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md"
              >
                Clear
              </button>
            </div>
          </form>
        </section>

        <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
          {error && <div className="mb-4 text-red-500">Error: {error}</div>}

          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="p-2">ID</th>
                  <th className="p-2">Customer</th>
                  <th className="p-2">Mikrotik ID</th>
                  <th className="p-2">Billing Status</th>
                  <th className="p-2">Info</th>
                  <th className="p-2">Admin Note</th>
                  <th className="p-2">Updated At</th>
                  <th className="p-2">publication period</th>
                  {role === "billing" && <th className="p-2">Action</th>}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="p-4 text-center">
                      Loading...
                    </td>
                  </tr>
                ) : paginated.length ? (
                  paginated.map((r) => (
                    <tr
                      key={r.id}
                      className="border-t border-gray-100 dark:border-gray-700"
                    >
                      <td className="p-2 text-sm">{r.id}</td>

                      <td className="p-2 text-sm font-medium">
                        {r.mikrotik_user}
                      </td>

                      <td className="p-2 text-sm text-gray-500">
                        {r.mikrotik_user_id}
                      </td>

                      <td className="p-2 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold
              ${
                r.billing_status === "paid"
                  ? "bg-green-100 text-green-700"
                  : r.billing_status === "enabled"
                  ? "bg-green-100 text-green-700"
                  : r.billing_status === "unpaid"
                  ? "bg-red-100 text-red-700"
                  : r.billing_status === "disabled"
                  ? "bg-red-100 text-red-700"
                  : "bg-yellow-100 text-yellow-700"
              }`}
                        >
                          {r.billing_status}
                        </span>
                      </td>

                      <td className="p-2 text-sm text-gray-600">
                        {r.note || "-"}
                      </td>
                      <td className="p-2 text-sm text-gray-600">
                        {r.note1 || "-"}
                      </td>

                      <td className="p-2 text-sm">
                        {new Date(r.updated_at).toLocaleString("id-ID")}
                      </td>

                      <td className="p-2 text-sm">
                        {new Date(r.created_at).toLocaleString("id-ID")}
                      </td>

                      {["billing", "admin", "NOC"].includes(role) && (
                        <td className="p-2 text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => openModal(r)}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleOpen(r)}
                              className="px-2 py-1 text-xs bg-yellow-500 text-white rounded"
                            >
                              Edit
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="p-4 text-center text-sm text-gray-500"
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {showModal && (
              <UserDetailModal data={selectedRow} onClose={closeModal} />
            )}

            <UpdateDisableListModal
              isOpen={open}
              data={selected}
              onClose={() => setOpen(false)}
              onSuccess={() => fetchData()}
            />
          </div>

          {/* pagination */}
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Total: {rows.length} rows
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded border bg-white dark:bg-gray-900"
              >
                Prev
              </button>
              <div className="px-3 py-1">
                {page} / {totalPages}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 rounded border bg-white dark:bg-gray-900"
              >
                Next
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
